<template>
    <multiselect-blade
        data-cy="target-selector-blade"
        class="target-selector-blade"
        :show-mask="showMask"
        :append-to="appendTo"
        :placement="placement"
        :position-strategy="positionStrategy"
        :value="internalSelectedTargets"
        :visible="true"
        :title="multiselectBladeTitle"
        :label="computedLabel"
        :selection-type-label="computedFilterType.id"
        :action-text="actionText"
        :close-on-mask-click="closeOnMaskClick"
        :loading="computedTargetOptionsLoading"
        :options="computedTargetOptions"
        :core-event-options="computedCoreEventOptions"
        :max="computedCurrentLimit"
        :filters="filters"
        :recent-event-options="computedRecentEventOptions"
        :is-single-select-blade="isSingleSelect"
        :show-select-all="showSelectAll"
        :show-close-button="showCloseButton"
        :sort-by-last-updated="computedFilterType.id === 'guide'"
        :has-group-sort-key="computedFilterType.id === 'guide'"
        :apply-disabled="internalApplyDisabled"
        option-sub-label-property="appDisplayName"
        :disabled-option-id="disabledOptionId"
        :disabled-tooltip="disabledTooltip"
        @input="onInput"
        @open="handleOpen"
        @close="onClose"
        @closed="$emit('closed')"
        @change-apply-disabled="handleApplyDisabledChange"
        @apply="onApply"
        @show-empty-state="destroyPopperInstance"
        @mouseenter="onOptionMouseenter"
        @mouseleave="onOptionMouseleave">
        <template
            #activityTypeFilter
            v-if="computedFilterOptions && computedFilterOptions.length > 1">
            <pendo-multiselect
                class="target-selector-blade__activity-type-filter"
                :allow-empty="false"
                :append-to-body="false"
                :aria-label="$t('targetSelectorBlade.Filter by activity type')"
                :full-width="true"
                :options="computedFilterOptions"
                :value="computedFilterType"
                @input="onFilterTypeChange">
                <template
                    #topLabel>
                    {{ $t('targetSelectorBlade.Source activity') }}
                </template>
                <template #option="{ option, index }">
                    <pendo-option-insert
                        :option="option"
                        :index="index" />
                </template>
                <template #selectedLabel="{ option }">
                    <pendo-option-insert :option="option" />
                </template>
            </pendo-multiselect>
        </template>
        <template #filters>
            <template v-if="computedFilterType.id === 'event'">
                <target-selector-blade-multiselect-filter
                    v-if="computedEventTypeList.length > 1"
                    class="target-selector-blade__event-type-filter"
                    data-cy="data-explorer-event-type-dropdown"
                    :value="eventTypeFilter"
                    :options="computedEventTypeList"
                    :placeholder="$t('targetSelectorBlade.Select an event type')"
                    :multiple-selection-label="$t('Event types')"
                    :aria-label="$t('targetSelectorBlade.Filter by event type')"
                    @input="handleFilterSelection('eventTypeFilter', $event)" />
                <target-selector-blade-multiselect-filter
                    data-cy="data-explorer-product-area-dropdown"
                    class="target-selector-blade__product-area-filter"
                    :value="productAreaFilter"
                    :options="computedProductAreaOptions"
                    :searchable="true"
                    label-key="name"
                    :placeholder="$t('targetSelectorBlade.Select a Product Area')"
                    :multiple-selection-label="$t('Product Areas')"
                    :aria-label="$t('targetSelectorBlade.Filter by product area')"
                    @input="handleFilterSelection('productAreaFilter', $event)" />
            </template>
            <div v-if="computedFilterType.id === 'guide'">
                <target-selector-blade-multiselect-filter
                    data-cy="data-explorer-guide-status-dropdown"
                    :value="guideStatusFilter"
                    :options="computedGuideStatusOptions"
                    label-key="name"
                    :placeholder="$t('targetSelectorBlade.Select a status')"
                    :multiple-selection-label="$t('Statuses')"
                    :aria-label="$t('targetSelectorBlade.Filter by status')"
                    @input="handleFilterSelection('guideStatusFilter', $event)" />
            </div>
            <div v-if="computedFilterType.id === 'app' && computedAppPlatformOptions.length > 1">
                <target-selector-blade-multiselect-filter
                    data-cy="data-explorer-app-platform-dropdown"
                    class="target-selector-blade__app-platform-filter"
                    :value="appPlatformFilter"
                    :options="computedAppPlatformOptions"
                    label-key="name"
                    :placeholder="$t('targetSelectorBlade.Select a platform')"
                    :multiple-selection-label="$t('Platforms')"
                    :aria-label="$t('targetSelectorBlade.Filter by platform')"
                    @input="handleFilterSelection('appPlatformFilter', $event)" />
            </div>
            <div
                v-if="showAppFilter"
                data-cy="data-explorer-app-dropdown">
                <target-selector-blade-multiselect-filter
                    :value="appFilter"
                    :options="allApps"
                    label-key="displayName"
                    :placeholder="$t('targetSelectorBlade.Select an app')"
                    :multiple-selection-label="$t('Apps')"
                    :aria-label="$t('targetSelectorBlade.Filter by app')"
                    @input="handleFilterSelection('appFilter', $event)" />
            </div>
            <div v-if="computedFilterType.id === 'guide' || computedFilterType.id === 'event'">
                <star-filter
                    :value="isStarredFilter"
                    @change="handleFilterSelection('isStarredFilter', $event)" />
            </div>
        </template>
        <template
            v-if="computedFilterType.id === 'event'"
            #popover>
            <div
                ref="popover"
                class="target-selector-blade-popover-wrapper"
                @mouseleave="onPopoverMouseleave">
                <div
                    v-if="showHoverCard"
                    class="target-selector-blade-popover-wrapper__inner">
                    <countable-hover-card
                        :id="countable.id"
                        :type="resourceTypeForHoverCard"
                        height="auto" />
                    <div class="target-selector-blade-popover__arrow" />
                </div>
            </div>
        </template>
    </multiselect-blade>
</template>

<script>
import { PendoTooltip, PendoMultiselect, PendoOptionInsert } from '@pendo/components';
import MultiselectBlade from '@/common/multiselect-blade/multiselect-blade.vue';
import TargetSelectorBladeMultiselectFilter from '@/common/target-selector-blade-multiselect-filter/target-selector-blade-multiselect-filter.vue';
import CountableHoverCard from '@/common/hover-card/countable-hover-card/countable-hover-card.vue';
import { createPopper } from '@popperjs/core';
import StarFilter from '@/common/filter-bar/filters/star-filter/star-filter.vue';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';
import { mapActions } from 'vuex';
import {
    RECENTLY_SELECTED_MAX_HISTORY,
    KIND_TO_RECENT_MAP,
    RECENTLY_SELECTED_HISTORY_TYPES,
} from '@/vuex/modules/user-settings/history/history.constants';
import {
    getFilterTypeFromTargets,
    getDefaultSelection,
    getTargetOptions,
    getEventOptions,
    createGroupedEventTargetOptions,
    createGroupedGuideTargetOptions,
    createProductAreaTargetOptions,
    mapFullProductAreasListToMiniList,
    sortRecentEventsList,
    getTargetType,
} from './target-selector-blade.utils';
import { getFilterChoiceConfig } from '@/guides/components/guide-management-overview-tab/config/filter-choices';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

const {
    PAGE_RECENTLY_SELECTED,
    FEATURE_RECENTLY_SELECTED,
    TRACKTYPE_RECENTLY_SELECTED,
    GUIDE_RECENTLY_SELECTED,
    AGENT_RECENTLY_SELECTED,
} = RECENTLY_SELECTED_HISTORY_TYPES;
const COUNTABLE_TARGETS = ['Feature', 'Page', 'TrackType', 'Guide', 'AIAgent'];

export default {
    name: 'TargetSelectorBlade',
    components: {
        TargetSelectorBladeMultiselectFilter,
        PendoOptionInsert,
        PendoMultiselect,
        MultiselectBlade,
        CountableHoverCard,
        StarFilter,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        /**
         * Configuration specifying which target types are available.
         * Event types (pages, features, trackEvents, agentEvents) automatically enable the events filter.
         * @example { apps: true, pages: true, features: true, trackEvents: true, agentEvents: false, guides: true, productArea: false }
         */
        targetTypesConfig: {
            type: Object,
            default: () => ({
                apps: true,
                pages: true,
                features: true,
                trackEvents: true,
                agentEvents: false,
                guides: true,
                productArea: false,
            }),
        },
        loadingTargetTypes: {
            type: Object,
            default: () => ({
                apps: true,
                pages: true,
                features: true,
                trackEvents: true,
                agentEvents: true,
                guides: true,
                productArea: true,
            }),
        },
        /**
         * Default filter type to show when opening the blade.
         * Options: 'apps', 'events', 'guides', 'productArea'
         */
        defaultFilterType: {
            type: String,
            default: 'events',
            validator: (value) => ['apps', 'events', 'guides', 'productArea'].includes(value),
        },
        appendTo: {
            type: [String, Element],
            default: undefined,
        },
        placement: {
            type: String,
            default: undefined,
        },
        positionStrategy: {
            type: String,
            default: undefined,
        },
        closeOnMaskClick: {
            type: Boolean,
            default: false,
        },
        allApps: {
            type: [Array, null],
            default: () => null,
        },
        allPages: {
            type: [Array, null],
            default: () => null,
        },
        allFeatures: {
            type: [Array, null],
            default: () => null,
        },
        allTrackEvents: {
            type: [Array, null],
            default: () => null,
        },
        allGuides: {
            type: [Array, null],
            default: () => null,
        },
        allAiAgents: {
            type: [Array, null],
            default: () => null,
        },
        allCoreEvents: {
            type: [Array, null],
            default: () => null,
        },
        allProductAreas: {
            type: [Array, null],
            default: () => null,
        },
        /**
         * Selection limits per filter type. Keys are filter types ('app', 'event', 'guide', 'productArea').
         * Values are the maximum number of items that can be selected for that type.
         * If a type is not specified, no limit is enforced (Infinity).
         * Use 1 for single-select mode.
         * @example { app: 1, event: 5, guide: 1, productArea: Infinity }
         */
        targetLimitByActivityType: {
            type: Object,
            default: () => ({}),
        },
        showSelectAll: {
            type: Boolean,
            default: true,
        },
        showMask: {
            type: Boolean,
            default: true,
        },
        actionText: {
            type: String,
            default: 'Apply',
        },
        recentPages: {
            type: Array,
            default: () => [],
        },
        recentFeatures: {
            type: Array,
            default: () => [],
        },
        recentTrackEvents: {
            type: Array,
            default: () => [],
        },
        recentGuides: {
            type: Array,
            default: () => [],
        },
        recentAgents: {
            type: Array,
            default: () => [],
        },
        initialTargets: {
            type: Array,
            default: () => [],
        },
        title: {
            type: [String, null],
            default: null,
        },
        disabledOptionId: {
            type: String,
            default: '',
        },
        disabledTooltip: {
            type: String,
            default: '',
        },
        showCloseButton: {
            type: Boolean,
            default: true,
        },
        hideHoverCard: {
            type: Boolean,
            default: false,
        },
    },
    /**
     * Events emitted by this component.
     *
     * - apply: Emitted when user applies their selection
     *   Payload: { targets: Array, filterType: Object, selectedTargets: Object }
     * - close: Emitted when the blade is closed
     * - closed: Emitted after the blade close animation completes
     * - open: Emitted when the blade is opened
     */
    emits: ['closed', 'close', 'open', 'apply'],
    setup () {
        const { queueMessage } = useAriaLiveRegistry();

        return { queueMessage };
    },
    data () {
        return {
            countable: null,
            countableElement: null,
            popperInstance: null,
            hoverTimer: null,
            // Internal state
            internalFilterType: null,
            internalSelectedTargets: {},
            internalBladeFilters: {},
            internalApplyDisabled: true,
        };
    },
    computed: {
        hasEventsEnabled () {
            const { pages, features, trackEvents, agentEvents } = this.targetTypesConfig;

            return pages || features || trackEvents || agentEvents;
        },
        /**
         * Derive the legacy targetTypesConfig format for utility functions (this can be updated once the legacy is removed)
         */
        derivedTargetTypesConfig () {
            return {
                apps: this.targetTypesConfig.apps,
                events: this.hasEventsEnabled,
                guides: this.targetTypesConfig.guides,
                productArea: this.targetTypesConfig.productArea,
            };
        },
        /**
         * Derive the legacy eventTypesConfig format for utility functions (this can be updated once the legacy is removed)
         */
        derivedEventTypesConfig () {
            return {
                pages: this.targetTypesConfig.pages,
                features: this.targetTypesConfig.features,
                trackEvents: this.targetTypesConfig.trackEvents,
                agentEvents: this.targetTypesConfig.agentEvents,
            };
        },
        computedFilterOptions () {
            return getTargetOptions(this.derivedTargetTypesConfig);
        },
        computedEventTypeList () {
            return getEventOptions(this.derivedEventTypesConfig);
        },
        computedGuideStatusOptions () {
            return getFilterChoiceConfig().statuses;
        },
        computedFilterType () {
            return this.internalFilterType || getDefaultSelection(this.defaultFilterType);
        },
        /**
         * Current selection limit based on the active filter type.
         * Returns Infinity if no limit is configured for the current type.
         */
        computedCurrentLimit () {
            const filterTypeId = this.computedFilterType.id;
            if (filterTypeId === 'app' && !this.multiApp) {
                return 1;
            }
            const limit = this.targetLimitByActivityType[filterTypeId];

            return typeof limit === 'number' ? limit : Infinity;
        },
        /**
         * Whether the current filter type is in single-select mode (limit of 1).
         */
        isSingleSelect () {
            return this.computedCurrentLimit === 1;
        },
        computedTargetOptionsLoading () {
            const loadingApps = this.loadingTargetTypes?.apps === undefined || this.loadingTargetTypes.apps === true;
            if (this.computedFilterType.id === 'app') {
                if (this.targetTypesConfig.apps && (this.allApps === null || loadingApps)) return true;
            }
            if (this.computedFilterType.id === 'event') {
                const loadingPages =
                    this.loadingTargetTypes?.pages === undefined || this.loadingTargetTypes.pages === true;
                const loadingFeatures =
                    this.loadingTargetTypes?.features === undefined || this.loadingTargetTypes.features === true;
                const loadingTrackEvents =
                    this.loadingTargetTypes?.trackEvents === undefined || this.loadingTargetTypes.trackEvents === true;
                const loadingAgentEvents =
                    this.loadingTargetTypes?.agentEvents === undefined || this.loadingTargetTypes.agentEvents === true;
                if (this.targetTypesConfig.pages && (this.allPages === null || loadingPages)) return true;
                if (this.targetTypesConfig.features && (this.allFeatures === null || loadingFeatures)) return true;
                if (this.targetTypesConfig.trackEvents && (this.allTrackEvents === null || loadingTrackEvents))
                {return true;}
                if (this.targetTypesConfig.agentEvents && (this.allAiAgents === null || loadingAgentEvents))
                {return true;}
                if (this.allApps === null || loadingApps) return true;
            }
            if (this.computedFilterType.id === 'guide') {
                const loadingGuides =
                    this.loadingTargetTypes?.guides === undefined || this.loadingTargetTypes.guides === true;
                if (this.targetTypesConfig.guides && (this.allGuides === null || loadingGuides)) return true;
                if (this.allApps === null || loadingApps) return true;
            }
            if (this.computedFilterType.id === 'productArea') {
                const loadingProductAreas =
                    this.loadingTargetTypes?.productArea === undefined || this.loadingTargetTypes.productArea === true;
                if (this.targetTypesConfig.productArea && (this.allProductAreas === null || loadingProductAreas))
                {return true;}
            }

            return false;
        },
        appsById () {
            return this.allApps !== null ? keyBy(this.allApps, 'id') : null;
        },
        computedTargetOptions () {
            if (this.computedTargetOptionsLoading || this.appsById === null) {
                return [];
            }

            if (this.computedFilterType.id === 'event') {
                // Only include event types that are enabled in targetTypesConfig
                let eventOptions = [];

                if (this.targetTypesConfig.pages && this.allPages) {
                    eventOptions = [...eventOptions, ...this.allPages];
                }
                if (this.targetTypesConfig.features && this.allFeatures) {
                    eventOptions = [...eventOptions, ...this.allFeatures];
                }
                if (this.targetTypesConfig.trackEvents && this.allTrackEvents) {
                    eventOptions = [...eventOptions, ...this.allTrackEvents];
                }
                if (this.targetTypesConfig.agentEvents && this.allAiAgents) {
                    eventOptions = [...eventOptions, ...this.allAiAgents];
                }

                return createGroupedEventTargetOptions(eventOptions, this.appsById);
            }

            if (this.computedFilterType.id === 'guide') {
                if (this.allGuides === null) return [];

                return createGroupedGuideTargetOptions({
                    guideStatusOptions: this.computedGuideStatusOptions,
                    allGuides: this.allGuides,
                    appsById: this.appsById,
                });
            }

            if (this.computedFilterType.id === 'productArea') {
                if (this.allProductAreas === null) return [];

                return mapFullProductAreasListToMiniList(this.allProductAreas);
            }

            return this.allApps;
        },
        computedProductAreaOptions () {
            // Product area filter is only relevant for the events filter type
            if (this.computedFilterType.id !== 'event') {
                return [];
            }

            return createProductAreaTargetOptions(this.computedTargetOptions);
        },
        computedRecentEventOptions () {
            const filterTypeId = this.computedFilterType.id;

            // No recent items for apps or product areas
            if (filterTypeId === 'app' || filterTypeId === 'productArea') {
                return [];
            }

            let recentEvents = [];

            // Only include recent guides when viewing guides
            if (filterTypeId === 'guide') {
                if (this.targetTypesConfig.guides) {
                    recentEvents = [...recentEvents, ...this.recentGuides];
                }

                return sortRecentEventsList(recentEvents);
            }

            // For events filter, include event types based on targetTypesConfig
            if (filterTypeId === 'event') {
                if (this.targetTypesConfig.pages) {
                    recentEvents = [...recentEvents, ...this.recentPages];
                }
                if (this.targetTypesConfig.features) {
                    recentEvents = [...recentEvents, ...this.recentFeatures];
                }
                if (this.targetTypesConfig.trackEvents) {
                    recentEvents = [...recentEvents, ...this.recentTrackEvents];
                }
                if (this.targetTypesConfig.agentEvents) {
                    recentEvents = [...recentEvents, ...this.recentAgents];
                }
            }

            return sortRecentEventsList(recentEvents);
        },
        computedCoreEventOptions () {
            const isEventFilter = this.computedFilterType.id === 'event';
            if (isEventFilter && this.allCoreEvents) {
                // only include core events of enabled types
                const coreEventTypes = [];
                if (this.targetTypesConfig.pages) {
                    coreEventTypes.push('pageViews');
                }
                if (this.targetTypesConfig.features) {
                    coreEventTypes.push('featureClicks');
                }
                if (this.targetTypesConfig.trackEvents) {
                    coreEventTypes.push('trackEvents');
                }
                if (this.targetTypesConfig.agentEvents) {
                    coreEventTypes.push('aiAgentInteractions');
                }

                return this.allCoreEvents.filter((coreEvent) => coreEventTypes.includes(getTargetType(coreEvent)));
            }

            return [];
        },
        computedLabel () {
            if (this.computedFilterType.id === 'app') {
                return 'any activity';
            }

            if (this.computedFilterType.id === 'event') {
                return 'event';
            }

            if (this.computedFilterType.id === 'guide') {
                return 'guide';
            }

            return this.computedFilterType.id;
        },
        multiselectBladeTitle () {
            return this.title || this.$t('targetSelectorBlade.Select activity');
        },
        eventTypeFilter () {
            return this.internalBladeFilters.eventTypeFilter || this.computedEventTypeList;
        },
        appFilter () {
            return this.internalBladeFilters.appFilter || this.allApps;
        },
        guideStatusFilter () {
            return this.internalBladeFilters.guideStatusFilter || this.computedGuideStatusOptions;
        },
        productAreaFilter () {
            return this.internalBladeFilters.productAreaFilter || this.computedProductAreaOptions;
        },
        appPlatformFilter () {
            return this.internalBladeFilters.appPlatformFilter || this.computedAppPlatformOptions;
        },
        isStarredFilter () {
            return !!this.internalBladeFilters.isStarredFilter;
        },
        /**
         * Available platform options derived from unique platforms in the apps list
         */
        computedAppPlatformOptions () {
            if (this.computedFilterType.id !== 'app' || !this.allApps) {
                return [];
            }

            const platformLabels = {
                web: this.$t('Web'),
                ios: this.$t('iOS'),
                android: this.$t('Android'),
                extension: this.$t('Extension'),
            };

            const uniquePlatforms = [...new Set(this.allApps.map((app) => app.platform).filter(Boolean))];

            return uniquePlatforms.map((platform) => ({
                id: platform,
                name: platformLabels[platform] || platform,
            }));
        },
        filters () {
            if (['productArea'].includes(this.computedFilterType.id)) {
                return [];
            }

            const filters = [];

            // Star filter only applies to events and guides
            if (this.computedFilterType.id === 'event' || this.computedFilterType.id === 'guide') {
                if (this.isStarredFilter) {
                    filters.push({
                        prop: 'isStarred',
                        value: this.isStarredFilter,
                    });
                }
            }

            if (this.multiApp && this.computedFilterType.id !== 'app' && this.appFilter) {
                if (this.appFilter.length > 0 && this.appFilter.length !== this.allApps.length) {
                    filters.push({
                        prop: 'appId',
                        value: this.appFilter.map(({ id }) => id),
                    });
                }
            }

            if (this.computedFilterType.id === 'event') {
                if (
                    this.eventTypeFilter &&
                    this.eventTypeFilter.length > 0 &&
                    this.eventTypeFilter.length !== this.computedEventTypeList.length
                ) {
                    filters.push({
                        prop: 'kind',
                        value: this.eventTypeFilter.map(({ kind }) => kind),
                    });
                }
                if (
                    this.productAreaFilter &&
                    this.productAreaFilter.length > 0 &&
                    this.productAreaFilter.length !== this.computedProductAreaOptions.length
                ) {
                    filters.push({
                        prop: 'group.id',
                        value: this.productAreaFilter.map(({ id }) => (id === 'noProductArea' ? '' : id)),
                    });
                }
            }
            if (this.computedFilterType.id === 'guide' && this.guideStatusFilter) {
                if (
                    this.guideStatusFilter.length > 0 &&
                    this.guideStatusFilter.length !== this.computedGuideStatusOptions.length
                ) {
                    filters.push({
                        prop: 'state',
                        value: this.guideStatusFilter.map(({ id }) => id),
                    });
                }
            }

            if (this.computedFilterType.id === 'app' && this.appPlatformFilter) {
                if (
                    this.appPlatformFilter.length > 0 &&
                    this.appPlatformFilter.length !== this.computedAppPlatformOptions.length
                ) {
                    filters.push({
                        prop: 'platform',
                        value: this.appPlatformFilter.map(({ id }) => id),
                    });
                }
            }

            return filters;
        },
        showAppFilter () {
            return (
                this.multiApp &&
                this.computedFilterType.id !== 'app' &&
                !['productArea'].includes(this.computedFilterType.id)
            );
        },
        multiApp () {
            return !!this.allApps && this.allApps.length > 1;
        },
        showHoverCard () {
            if (this.$productLine(PRODUCT_LINES.ADOPT)) {
                return false;
            }

            if (this.hideHoverCard) {
                return false;
            }
            if (this.countable?.type !== 'option') {
                return false;
            }

            return ['Page', 'Feature', 'TrackType'].includes(this.countable?.kind);
        },
        resourceTypeForHoverCard () {
            switch (this.countable?.kind) {
                case 'Page':
                    return 'page';
                case 'Feature':
                    return 'feature';
                case 'TrackType':
                    return 'trackEvent';
                default:
                    return null;
            }
        },
    },
    watch: {
        computedTargetOptionsLoading: {
            handler (newLoading) {
                if (newLoading === false) {
                    this.setupSelectedOptions();
                }
            },
        },
    },
    created () {
        this.initializeState();
    },
    beforeUnmount () {
        this.countable = null;
        this.countableElement = null;
        clearTimeout(this.hoverTimer);
        this.destroyPopperInstance();
    },
    methods: {
        ...mapActions({
            updateBatchMostRecent: 'userSettings/history/updateBatchMostRecent',
        }),
        initializeState () {
            let filterType = null;
            // Set initial filter type based on initial targets
            if (this.initialTargets.length > 0) {
                filterType = getFilterTypeFromTargets(this.initialTargets);
            }

            // If no filter was found or could not be determined from the targets, use the default (as long as it is enabled)
            if (!filterType) {
                const defaultsToEvents = this.hasEventsEnabled && this.defaultFilterType === 'events';
                if (defaultsToEvents || this.targetTypesConfig[this.defaultFilterType]) {
                    filterType = getDefaultSelection(this.defaultFilterType);
                } else if (this.hasEventsEnabled) {
                    filterType = getDefaultSelection('events');
                } else if (this.targetTypesConfig.apps) {
                    filterType = getDefaultSelection('apps');
                } else if (this.targetTypesConfig.guides) {
                    filterType = getDefaultSelection('guides');
                } else if (this.targetTypesConfig.productArea) {
                    filterType = getDefaultSelection('productArea');
                }
            }
            this.internalFilterType = filterType;
            // Reset internal state
            this.internalBladeFilters = {};
            this.internalApplyDisabled = true;

            // Setup selected options after filter type is set
            this.$nextTick(() => {
                this.setupSelectedOptions();
            });
        },
        setupSelectedOptions () {
            if (this.computedTargetOptionsLoading) return;

            this.internalSelectedTargets = this.getSelectedTargetEntities(this.initialTargets);
        },
        getSelectedTargetEntities (targets) {
            if (this.computedTargetOptionsLoading || !targets) return;

            let options = [];
            if (this.computedFilterType.id === 'event') {
                // Only include event types that are enabled in targetTypesConfig
                if (this.targetTypesConfig.pages && this.allPages) {
                    options = [...options, ...this.allPages];
                }
                if (this.targetTypesConfig.features && this.allFeatures) {
                    options = [...options, ...this.allFeatures];
                }
                if (this.targetTypesConfig.trackEvents && this.allTrackEvents) {
                    options = [...options, ...this.allTrackEvents];
                }
                if (this.targetTypesConfig.agentEvents && this.allAiAgents) {
                    options = [...options, ...this.allAiAgents];
                }
            } else if (this.computedFilterType.id === 'guide') {
                options = this.allGuides || [];
            } else if (this.computedFilterType.id === 'productArea') {
                options = this.allProductAreas || [];
                options = options.map((el) => {
                    return { ...el, kind: 'productArea' };
                });
            } else if (this.computedFilterType.id === 'app') {
                options = this.allApps || [];
            }

            const optionsById = keyBy(options, 'id');
            const targetIds = targets.map(({ id }) => id);

            return cloneDeep(pick(optionsById, targetIds));
        },
        destroyPopperInstance () {
            const body = document.querySelector('body');
            if (this.$refs.popover?.parentNode === body) {
                body.removeChild(this.$refs.popover);
            }
            this.popperInstance?.destroy();
            this.popperInstance = null;
        },
        handleOpen () {
            this.$emit('open');
        },
        handleApplyDisabledChange (value) {
            this.internalApplyDisabled = value;
        },
        handleFilterSelection (filter, value) {
            this.internalBladeFilters = {
                ...this.internalBladeFilters,
                [filter]: value,
            };
        },
        onFilterTypeChange (option) {
            if (option.id !== this.computedFilterType.id) {
                this.internalSelectedTargets = {};
                this.internalFilterType = option;
                if (option.id !== 'event') {
                    this.destroyPopperInstance();
                }
            }
        },
        onClose () {
            this.hidePopover();
            this.destroyPopperInstance();
            this.resetInternalState();
            this.$emit('close');
        },
        resetInternalState () {
            this.internalFilterType = null;
            this.internalSelectedTargets = {};
            this.internalBladeFilters = {};
            this.internalApplyDisabled = true;
        },
        createPopperInstance (element) {
            document.querySelector('body').appendChild(this.$refs.popover);

            this.popperInstance = createPopper(element, this.$refs.popover, {
                strategy: 'fixed',
                placement: 'right-start',
                modifiers: [
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            fallbackPlacements: ['right-start', 'right-end', 'left-start', 'left-end'],
                        },
                    },
                    {
                        enabled: true,
                        name: 'preventOverflow',
                        options: {
                            boundary: document.querySelector('body'),
                        },
                    },
                ],
            });
        },
        onOptionMouseenter ({ option, element }) {
            if (this.computedFilterType.id !== 'event') {
                return;
            }

            // prevent flashing hover card if user runs cursor down list of options quickly
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
            }

            if (this.countable?.id === option.id) {
                return;
            }

            this.$refs.popover?.removeAttribute('data-show');

            this.countable = option;
            this.countableElement = element;

            if (!this.popperInstance) {
                this.createPopperInstance(element);
            }

            this.hoverTimer = setTimeout(() => {
                this.$refs.popover?.setAttribute('data-show', '');
                this.popperInstance.state.elements.reference = element;
                this.popperInstance.update();
            }, 300);
        },
        onOptionMouseleave ($event) {
            if (this.computedFilterType.id !== 'event') {
                return;
            }

            if (this.$refs.popover?.contains($event.relatedTarget) && this.countableElement) {
                this.countableElement.classList.add('is-hover');

                return;
            }

            this.hidePopover();
        },
        onApply () {
            const recentlySelectedItems = this.getRecentlySelectedItemsByKind();
            this.updateRecentlySelected(recentlySelectedItems);

            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
            }
            this.destroyPopperInstance();

            this.queueMessage({ message: this.$t('targetSelectorBlade.Filters_applied'), ariaLiveMode: 'polite' });
            this.$emit('apply', {
                selectedTargets: this.getSelectedTargetEntities(Object.values(this.internalSelectedTargets)),
                filterType: this.computedFilterType,
                filters: this.internalBladeFilters,
            });
        },
        onPopoverMouseleave ($event) {
            if (this.countableElement) {
                this.countableElement.classList.remove('is-hover');
            }

            if ($event.relatedTarget === this.countableElement) {
                return;
            }

            this.hidePopover();
        },
        hidePopover () {
            this.$refs.popover?.removeAttribute('data-show');
            this.countable = null;
            this.countableElement = null;
        },
        onInput ($event) {
            this.internalSelectedTargets = $event;

            const recentlySelectedItem = Object.values($event)[0];

            if (recentlySelectedItem) {
                const isCountableTarget = COUNTABLE_TARGETS.includes(recentlySelectedItem.kind);

                if (this.isSingleSelect && isCountableTarget) {
                    const kindBucket = KIND_TO_RECENT_MAP[recentlySelectedItem.kind];
                    this.updateRecentlySelected({
                        [kindBucket]: [recentlySelectedItem.id],
                    });
                }
            }

            if (this.isSingleSelect) {
                if (this.hoverTimer) {
                    clearTimeout(this.hoverTimer);
                }
                this.destroyPopperInstance();

                // Auto-apply in single select mode
                this.$emit('apply', {
                    selectedTargets: this.getSelectedTargetEntities(Object.values($event)),
                    filterType: this.computedFilterType,
                    filters: this.internalBladeFilters,
                });
            }
        },
        getRecentlySelectedItemsByKind () {
            const initialTargetMap = this.initialTargets.reduce((acc, target) => {
                acc[target.id] = target;

                return acc;
            }, {});

            const selectedTargets = this.internalSelectedTargets;

            return Object.values(selectedTargets).reduce(
                (acc, target) => {
                    const isCountableTarget = COUNTABLE_TARGETS.includes(target?.kind);

                    if (isCountableTarget && !initialTargetMap[target.id]) {
                        const kindBucket = KIND_TO_RECENT_MAP[target.kind];
                        acc[kindBucket].push(target.id);
                    }

                    return acc;
                },
                {
                    [PAGE_RECENTLY_SELECTED]: [],
                    [FEATURE_RECENTLY_SELECTED]: [],
                    [TRACKTYPE_RECENTLY_SELECTED]: [],
                    [GUIDE_RECENTLY_SELECTED]: [],
                    [AGENT_RECENTLY_SELECTED]: [],
                },
            );
        },
        updateRecentlySelected (recentSelectionBuckets) {
            Object.keys(recentSelectionBuckets).forEach((key) => {
                const ids = recentSelectionBuckets[key];

                if (ids.length) {
                    this.updateBatchMostRecent({
                        ids,
                        type: key,
                        maxHistory: RECENTLY_SELECTED_MAX_HISTORY,
                    });
                }
            });
        },
    },
};
</script>

<style lang="less" scoped>
.target-selector-blade {
    &__event-type-filter {
        padding-right: 4px;
    }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.target-selector-blade-popover-wrapper {
    position: absolute;
    z-index: 10001;
    width: max-content;
    top: 0;
    left: 0;
    display: none;

    &[data-show] {
        display: block;
    }

    .hover-card {
        border: none;
        box-shadow: none;
    }

    &__inner {
        border: 1px solid var(--palette-gray-40);
        background-color: var(--palette-gray-0);
        border-radius: 3px;
        filter: drop-shadow(var(--semantic-drop-shadow-base) 0 1px 3px);
    }

    &[data-popper-placement^='right'] {
        padding-left: 8px;

        .target-selector-blade-popover__arrow,
        .target-selector-blade-popover__arrow:before {
            border-width: 8px 8px 8px 0;
            border-top-color: transparent !important;
            border-left-color: transparent !important;
            border-bottom-color: transparent !important;
        }

        .target-selector-blade-popover__arrow {
            left: -8px;
        }

        .target-selector-blade-popover__arrow:before {
            left: 1px;
        }
    }

    &[data-popper-placement^='left'] {
        padding-right: 8px;

        .target-selector-blade-popover__arrow,
        .target-selector-blade-popover__arrow:before {
            border-width: 8px 0 8px 8px;
            border-top-color: transparent !important;
            border-right-color: transparent !important;
            border-bottom-color: transparent !important;
        }

        .target-selector-blade-popover__arrow {
            right: -8px;
        }

        .target-selector-blade-popover__arrow:before {
            right: 1px;
        }
    }

    &[data-popper-placement='left-start'] {
        .target-selector-blade-popover__arrow {
            top: 18px;
        }
    }
    &[data-popper-placement='left-end'] {
        .target-selector-blade-popover__arrow {
            bottom: 18px;
        }
    }
    &[data-popper-placement='right-start'] {
        .target-selector-blade-popover__arrow {
            top: 18px;
        }
    }
    &[data-popper-placement='right-end'] {
        .target-selector-blade-popover__arrow {
            bottom: 18px;
        }
    }

    .target-selector-blade-popover__arrow,
    .target-selector-blade-popover__arrow:before {
        margin-left: 0;
        margin-right: 0;
    }

    .target-selector-blade-popover__arrow {
        width: 0;
        height: 0;
        border-style: solid;
        position: absolute;
        z-index: 1;
        border-color: var(--palette-gray-40);

        &:before {
            top: calc(50% - 8px);
            content: '';
            width: 0;
            height: 0;
            border-style: solid;
            position: absolute;
            z-index: 0;
            border-color: var(--palette-gray-0);
        }
    }
}
</style>
